import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [role, setRole] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('Token no proporcionado');
      return;
    }

    const verifyToken = async () => {
      try {
        // Clear ALL old tokens and user data before setting new ones
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        localStorage.removeItem('worker_token');
        localStorage.removeItem('worker_user');
        localStorage.removeItem('client_token');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('user');
        
        const response = await axios.get(`${API_URL}/api/unified-auth/verify?token=${token}`);
        
        // Prepare user data for the callback
        const userData = {
          email: response.data.email,
          role: response.data.role,
          system_role: response.data.system_role,
          user_id: response.data.user_id,
          id: response.data.user_id,
          worker_id: response.data.role === 'worker' ? response.data.user_id : null,
          client_id: response.data.role === 'client' ? response.data.user_id : null,
          name: response.data.name
        };
        
        setRole(response.data.role);
        setStatus('success');

        // Include has_pin flag for onboarding flow
        const callbackParams = new URLSearchParams({
          token: response.data.token,
          role: response.data.role,
          redirect: response.data.redirect_path,
          user: encodeURIComponent(JSON.stringify(userData)),
          has_pin: response.data.has_pin ? '1' : '0'
        });
        
        navigate(`/auth/callback?${callbackParams.toString()}`, { replace: true });

      } catch (error) {
        setStatus('error');
        setError(error.response?.data?.detail || 'Error al verificar el enlace');
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  const roleNames = {
    admin: 'Panel de Administracion',
    worker: 'Portal del Colaborador',
    client: 'Portal del Cliente'
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-[#e03c3c] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Verificando acceso...</h2>
              <p className="text-gray-500">Un momento por favor</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Acceso verificado</h2>
              <p className="text-gray-500 mb-4">
                Redirigiendo a {roleNames[role] || 'tu panel'}...
              </p>
              <div className="flex justify-center">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Error de verificacion</h2>
              <p className="text-gray-500 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-[#e03c3c] hover:bg-[#c73535] text-white rounded-xl font-medium transition-colors"
              >
                Volver al inicio
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
